import { isOperationOutcome } from "commons"
import { OperationOutcome, Parameters, ResourceObject } from "commons/types"

class Client {
  private apiUrl?: string
  private token?: string

  constructor(token?: string, apiUrl?: string) {
    this.apiUrl = apiUrl ? apiUrl : window.VITE_APP_API
    this.token = token
  }

  request = async (endpoint: string, { body, headers: customHeaders, ...restConfig }: RequestInit) => {
    const config = {
      body,
      headers: {
        ...(this.token ? { Authorization: this.token } : {}),
        ...(body ? { "Content-Type": "application/json" } : {}),
        ...customHeaders,
      },
      ...restConfig,
    }

    const response = await fetch(`${this.apiUrl}/${endpoint}`, config)

    const data = await response.json().catch(() => {})

    if (response.ok) {
      return data
    }

    switch (response.status) {
      case 400:
        if (isOperationOutcome(data)) {
          const { message, issues } = this.getErrorFromOO(data)

          throw new Error("Bad request", { cause: { name: "400", message, issues } })
        }

        throw new Error("Not supported", {
          cause: { name: "not-supported", message: `Invalid response: ${response.statusText} ${response.status}` },
        })
      case 401:
        if (data?.message?.includes("JWT is expired")) {
          throw new Error("Forbidden", { cause: { name: "403", message: "Token expired" } })
        }

        throw new Error("Unauthorized", { cause: { name: "401", message: "Unauthorized" } })
      case 402:
        if (isOperationOutcome(data)) {
          const { message, issues } = this.getErrorFromOO(data)

          throw new Error("Payment required", { cause: { name: "402", message, issues } })
        }

        throw new Error("Not supported", {
          cause: { name: "not-supported", message: `Invalid response: ${response.statusText} ${response.status}` },
        })
      case 403:
        throw new Error("Forbidden", { cause: { name: "403", message: "Forbidden" } })
      default:
        if (isOperationOutcome(data)) {
          const { message, issues } = this.getErrorFromOO(data)

          throw new Error("Internal server error", { cause: { name: "500", message, issues } })
        }

        throw new Error("Not supported", {
          cause: { name: "not-supported", message: `Invalid response: ${response.statusText} ${response.status}` },
        })
    }
  }

  read = async <T>(endpoint: string) => {
    const data = await this.request(endpoint, {
      method: "GET",
    })

    return data as T
  }

  post = async <T, R = T>(endpoint: string, resource: T, headers?: HeadersInit) => {
    const data = await this.request(endpoint, {
      method: "POST",
      body: JSON.stringify(resource),
      headers,
    })

    return data as R
  }

  update = async <T, R = T>(endpoint: string, resource: T, headers?: HeadersInit) => {
    const data = await this.request(endpoint, {
      method: "PUT",
      body: JSON.stringify(resource),
      headers,
    })

    return data as R
  }

  put = async <T, R = T>(endpoint: string, resource: T, headers?: HeadersInit) =>
    this.update<T, R>(endpoint, resource, headers)

  operationRequest = async <T extends ResourceObject>(
    endpoint: string,
    method: "GET" | "POST" | "PATCH" | "PUT" = "GET",
    id?: string,
    parameters?: Parameters,
    filters?: URLSearchParams,
    operation?: string,
    signal?: AbortSignal,
    headers?: HeadersInit,
  ) => {
    const data = await this.request(
      `${endpoint}${id ? `/${id}` : ""}${operation !== "" ? `${endpoint || id ? "/" : ""}$${operation}` : ""}${
        filters ? `?${filters}` : ""
      }`,
      {
        method: method,
        body: JSON.stringify(parameters),
        ...(method === "GET" ? { signal } : {}),

        headers,
      },
    )

    return data as T
  }

  getErrorFromOO = (oo: OperationOutcome) => {
    let message = `${oo.text?.div ?? "Something went wrong"}`

    // Delete response body data from error message for security
    message = message.split("Response body")[0]

    const issues = oo.issue?.reduce((prev, issue) => {
      if (issue.details?.text || issue.diagnostics) {
        const detail = issue.details?.text ?? issue.diagnostics ?? "no details provided"

        return [...prev, detail]
      }

      return prev
    }, [] as string[])

    return { message, issues }
  }
}

export { Client }
